import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  ListResponseOfProductVersion,
  ProductVersion,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import ProductVersionCreateForm from "./ProductVersionCreateForm";
import ProductVersionUpdateForm from "./ProductVersionUpdateForm";

interface ProductVersionWithId extends ProductVersion {
  id: string;
}

const ProductVersions = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { productService } = useGlobalContext();
  const { registerPromise } = useTrashablePromises();
  const [createOpen, createOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [editing, setEditing] = useState<ProductVersion | null>(null);
  const [deleting, setDeleting] = useState<ProductVersion | null>(null);
  const searchTerm = persistanceStore.useState(
    (s) => s.productVersionFilterSearchTerm
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfProductVersion>(
    ["productVersions", debouncedSearchTerm, page, rowsPerPage, order, orderBy],
    () =>
      productService.getProductVersions(
        AdminListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            searchText: debouncedSearchTerm,
          },
        })
      )
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm]);

  useListTitle("productVersionsTitle");

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const onProductVersionCreated = () => {
    queryClient.invalidateQueries("productVersions");
    createOpenActions.setFalse();
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "productVersionCreated" }))
    );
  };

  const onProductVersionUpdated = () => {
    queryClient.invalidateQueries("productVersions");
    setEditing(null);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "productVersionUpdated" }))
    );
  };

  const handleDelete = () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id)
      return registerPromise(productService.deleteProductVersion(id))
        .then(() => {
          queryClient.invalidateQueries("productVersions");

          setSnackbarMessage(
            new SnackbarMessageProps(
              t({ id: "productVersionDeleted" }),
              Severity.Warning
            )
          );
        })
        .catch((err: any) => {
          console.log(err);
          setSnackbarMessage(
            new SnackbarMessageProps(translateError(err, t), Severity.Error)
          );
        });
  };

  const UpdateButton = ({ value }: { value: ProductVersion }) => {
    return (
      <Box textAlign="center">
        <IconButton size="small" onClick={() => setEditing(value)}>
          <EditIcon />
        </IconButton>
      </Box>
    );
  };

  const productVersions = (data?.data ?? []) as ProductVersionWithId[];

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "value",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "valueLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: UpdateButton,
      label: "",
      noSort: true,
    },
  ];

  const setTerm = (term: string) => {
    persistanceStore.update((s) => {
      s.productVersionFilterSearchTerm = term;
    });
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Fab
          aria-label="add"
          color="primary"
          onClick={createOpenActions.setTrue}
          data-cy="addButton"
        >
          <AddIcon />
        </Fab>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setTerm("")}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => setTerm(e.target.value)}
          data-cy="searchText"
        />
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: productVersions,
          loading: isFetching,
          title: t({ id: "productVersionsTitle" }),

          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <ProductVersionCreateForm
          onProductVersionCreated={onProductVersionCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>

      <Dialog open={!!editing} disableEnforceFocus maxWidth="sm" fullWidth>
        {editing && (
          <ProductVersionUpdateForm
            productVersion={editing}
            onProductVersionUpdated={onProductVersionUpdated}
            onCancel={() => setEditing(null)}
          />
        )}
      </Dialog>

      <Dialog open={!!deleting} disableEnforceFocus>
        <DialogTitle>
          <T id="productVersionDeleteTitle" />
        </DialogTitle>
        <DialogContent dividers>
          <T id="productVersionDeleteInfo" />
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setDeleting(null)} color="primary">
            <T id="cancelButton" />
          </Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">
            <T id="deleteButton" />
          </Button>
        </DialogActions>
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default ProductVersions;
